/**
 * Accessibility Fix for aria-hidden conflicts
 * Prevents aria-hidden on elements that contain focused inputs
 */

class AccessibilityFix {
  constructor() {
    this.observers = [];
    this.eventListeners = [];
    this.abortController = new AbortController();
    this.init();
  }

  init() {
    // Fix aria-hidden conflicts with focused elements
    this.fixAriaHiddenConflicts();
    
    // Fix links without accessible names
    this.fixLinkNames();
    
    // Fix color contrast issues
    this.fixColorContrast();
    
    // Monitor for dynamic content changes
    this.observeDOMChanges();
    
    // Monitor focus events
    this.monitorFocusEvents();
    
    // Re-run fixes after DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.fixLinkNames();
      });
    } else {
      this.fixLinkNames();
    }
  }

  fixAriaHiddenConflicts() {
    // Cache DOM query to avoid repeated queries
    const ariaHiddenElements = document.querySelectorAll('[aria-hidden="true"]');
    if (ariaHiddenElements.length === 0) return;
    
    // Cache focusable selectors
    const focusableSelectors = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    ariaHiddenElements.forEach(element => {
      // Fix Bug 1: Skip decorative links - they are handled by fixLinkNames()
      // Decorative links with focusable children get aria-label but no aria-hidden,
      // and we don't want fixAriaHiddenConflicts() to interfere with that logic
      if (element.classList.contains('t4s-full-width-link') || element.classList.contains('decorative')) {
        return; // Skip decorative links
      }
      
      // Check if this element or its descendants contain focusable elements (not just focused)
      const focusableElements = element.querySelectorAll(focusableSelectors);
      
      // Also check if element itself is focusable (matches() only accepts a single selector)
      let isElementFocusable = false;
      if (element.matches) {
        // Check each selector type individually since matches() doesn't accept comma-separated selectors
        isElementFocusable = element.matches('a[href]') || 
                            element.matches('button') || 
                            element.matches('input') || 
                            element.matches('select') || 
                            element.matches('textarea') || 
                            (element.hasAttribute('tabindex') && element.getAttribute('tabindex') !== '-1');
      }
      
      if (focusableElements.length > 0 || isElementFocusable) {
        // Remove aria-hidden to prevent accessibility issues
        element.removeAttribute('aria-hidden');
      }
    });

    // Special fix for login sidebar and modals
    const loginSidebar = document.getElementById('t4s-login-sidebar');
    if (loginSidebar) {
      const emailInput = loginSidebar.querySelector('#CustomerEmail');
      if (emailInput && (emailInput === document.activeElement || loginSidebar.classList.contains('is-open'))) {
        // Remove aria-hidden to make sidebar accessible (aria-hidden="false" is treated as true by assistive technologies)
        loginSidebar.removeAttribute('aria-hidden');
      }
    }
    
    // Fix modals and drawers that are open (use specific selectors to avoid unintended matches)
    // More specific: use exact class matches first, then validate partial matches
    const exactSelectors = '.modal.is-open, .modal.open, .drawer.is-open, .drawer.open';
    const exactModals = document.querySelectorAll(exactSelectors);
    const processedElements = new Set(); // Track elements already processed by exact selectors
    exactModals.forEach(modal => {
      if (modal.hasAttribute('aria-hidden') && modal.getAttribute('aria-hidden') === 'true') {
        modal.removeAttribute('aria-hidden');
      }
      processedElements.add(modal); // Mark as processed
    });
    
    // For partial class matches, validate that the element is actually a modal/drawer
    // Fix: Selector matches elements with separate classes (e.g., class="modal is-open")
    // but validation was checking for single class containing both patterns
    const partialModals = document.querySelectorAll('[class*="modal"][class*="is-open"], [class*="drawer"][class*="is-open"], [class*="modal"][class*="open"], [class*="drawer"][class*="open"]');
    partialModals.forEach(modal => {
      // Skip if already processed by exact selector (check Set, not class name)
      if (processedElements.has(modal)) {
        return;
      }
      
      // Additional validation: ensure it's actually a modal/drawer element
      // Fix: Check for modal/drawer class AND is-open/open class separately (not in same class name)
      const classList = Array.from(modal.classList);
      const hasModalClass = classList.some(cls => {
        // Fix Bug 1: Check for modal/drawer classes but exclude specific negative patterns
        // Only reject classes that explicitly negate modal/drawer (e.g., 'no-modal', 'not-modal')
        // Don't reject classes that just happen to contain 'no' (e.g., 'animation', 'notification')
        const isModalOrDrawer = cls.includes('modal') || cls.includes('drawer');
        const isNegativePattern = cls.includes('no-modal') || cls.includes('not-modal') || 
                                  cls.includes('no-drawer') || cls.includes('not-drawer') ||
                                  cls.includes('unrelated');
        return isModalOrDrawer && !isNegativePattern;
      });
      // Fix: Check for exact class matches, not substring matches
      // This prevents false positives like 'reopened', 'unopened', 'openform', etc.
      const hasOpenClass = classList.some(cls => 
        cls === 'is-open' || cls === 'open'
      );
      const hasModalPattern = hasModalClass && hasOpenClass;
      
      // Also check for common modal/drawer attributes or roles
      const hasModalRole = modal.getAttribute('role') === 'dialog' || 
                          modal.getAttribute('role') === 'alertdialog' ||
                          modal.hasAttribute('data-modal') ||
                          modal.hasAttribute('data-drawer');
      
      // Fix: Process elements matching the selector even without role if they have modal pattern
      // The selector already validated they have both modal/drawer AND is-open/open classes
      if ((hasModalPattern || hasModalRole) && modal.hasAttribute('aria-hidden') && modal.getAttribute('aria-hidden') === 'true') {
        // Remove aria-hidden to make modal accessible (aria-hidden="false" is treated as true by assistive technologies)
        modal.removeAttribute('aria-hidden');
      }
    });
  }

  observeDOMChanges() {
    // Use MutationObserver to watch for dynamic content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target;
          if (target.getAttribute('aria-hidden') === 'true') {
            // Check if this element now contains focused elements
            // Fix: :focus pseudo-class doesn't work in querySelectorAll - use document.activeElement instead
            const activeElement = document.activeElement;
            if (activeElement && target.contains(activeElement)) {
              // Check if active element is a focusable element
              // Fix: matches() only accepts single selector, not comma-separated list - check each individually
              const isFocusable = activeElement.matches('input') ||
                                  activeElement.matches('button') ||
                                  activeElement.matches('select') ||
                                  activeElement.matches('textarea') ||
                                  activeElement.matches('a[href]') ||
                                  activeElement.matches('[tabindex]:not([tabindex="-1"])');
              if (isFocusable) {
                target.removeAttribute('aria-hidden');
                // Fixed dynamic aria-hidden conflict
              }
            }
          }
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true
    });
    
    // Store observer for cleanup
    this.observers.push(observer);
  }

  monitorFocusEvents() {
    // Listen for focus events on potentially problematic elements
    const focusHandler = (event) => {
      const focusedElement = event.target;
      
      // Check if the focused element is inside an aria-hidden container
      let parent = focusedElement.parentElement;
      while (parent) {
        if (parent.getAttribute('aria-hidden') === 'true') {
          // Remove aria-hidden from the parent
          parent.removeAttribute('aria-hidden');
          break;
        }
        parent = parent.parentElement;
      }
    };
    
    const loginSidebarHandler = (event) => {
      const focusedElement = event.target;
      const loginSidebar = document.getElementById('t4s-login-sidebar');
      
      if (loginSidebar && loginSidebar.contains(focusedElement)) {
        if (loginSidebar.getAttribute('aria-hidden') === 'true') {
          loginSidebar.removeAttribute('aria-hidden');
        }
      }
    };

    document.addEventListener('focusin', focusHandler, { signal: this.abortController.signal });
    document.addEventListener('focusin', loginSidebarHandler, { signal: this.abortController.signal });
    
    // Store handlers for cleanup
    this.eventListeners.push(
      { type: 'focusin', handler: focusHandler, element: document },
      { type: 'focusin', handler: loginSidebarHandler, element: document }
    );
  }
  
  destroy() {
    // Disconnect all observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    
    // Remove all event listeners
    this.eventListeners.forEach(({ type, handler, element }) => {
      element.removeEventListener(type, handler);
    });
    this.eventListeners = [];
    
    // Abort all signal-based listeners
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  fixLinkNames() {
    // Find all links without accessible names - cache query
    const links = document.querySelectorAll('a[href]:not([aria-label]):not([aria-labelledby])');
    if (links.length === 0) return;
    
    // Cache focusable selectors
    const focusableSelectors = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    links.forEach(link => {
      // Skip if link has visible text
      const text = link.textContent.trim();
      if (text && text.length > 0) return;
      
      // Skip if link has title attribute
      if (link.getAttribute('title')) return;
      
      // Fix Bug 2: Decorative/empty links should be hidden from screen readers
      // However, if they contain focusable children, fixAriaHiddenConflicts() will remove aria-hidden
      // To prevent inaccessible links, we need to either:
      // 1. Assign an aria-label before marking as hidden, OR
      // 2. Ensure they don't have focusable descendants
      // We'll assign a generic aria-label to ensure accessibility if aria-hidden is later removed
        if (link.classList.contains('t4s-full-width-link') || link.classList.contains('decorative')) {
          // Fix Bug 2: Check if link contains focusable descendants that would cause aria-hidden to be removed
          const hasFocusableChildren = link.querySelectorAll(focusableSelectors).length > 0;
        
        if (hasFocusableChildren) {
          // Fix Bug 1: If decorative link has focusable children, assign aria-label instead of hiding
          // This ensures the link has an accessible name if it remains visible
          // fixAriaHiddenConflicts() will skip decorative links, so aria-hidden won't be removed
          if (!link.hasAttribute('aria-label')) {
            link.setAttribute('aria-label', 'Decorative link');
          }
          // Don't set aria-hidden since it would hide the focusable children
        } else {
          // Fix Bug 2: Safe to hide - no focusable children that would cause aria-hidden to be removed
          if (!link.hasAttribute('aria-hidden')) {
            link.setAttribute('aria-hidden', 'true');
          }
        }
        return;
      }
      
      // Try to find accessible name from:
      // 1. Image alt text
      const img = link.querySelector('img');
      if (img && img.alt) {
        link.setAttribute('aria-label', img.alt);
        return;
      }
      
      // 2. Icon class or data attribute
      const icon = link.querySelector('[class*="icon"], [class*="Icon"], svg, [data-icon]');
      if (icon) {
        let iconName = icon.getAttribute('data-icon') || 
                       icon.getAttribute('aria-label');
        
        // Safely extract from className (handle both string and DOMTokenList)
        if (!iconName) {
          const className = typeof icon.className === 'string' 
            ? icon.className 
            : (icon.className?.baseVal || icon.className?.value || '');
          const match = String(className).match(/(?:icon|Icon)-([a-z-]+)/i);
          iconName = match ? match[1] : null;
        }
        
        iconName = iconName || 'link';
        link.setAttribute('aria-label', String(iconName).replace(/-/g, ' '));
        return;
      }
      
      // 3. Generate from href
      const href = link.getAttribute('href');
      if (href && href !== '#' && !href.startsWith('javascript:')) {
        const hrefName = href.split('/').pop().replace(/[-_]/g, ' ').replace(/\.[^.]+$/, '');
        if (hrefName && hrefName.length > 0) {
          link.setAttribute('aria-label', hrefName.charAt(0).toUpperCase() + hrefName.slice(1));
        } else {
          // Fallback for empty href links
          link.setAttribute('aria-label', 'Link');
        }
      } else if (href === '#' || !href) {
        // Empty or hash-only links should have aria-label
        link.setAttribute('aria-label', 'Link');
      } else if (href && href.startsWith('javascript:')) {
        // JavaScript protocol links need accessible names (extract action from JavaScript code if possible)
        const jsCode = href.replace(/^javascript:/i, '').trim();
        if (jsCode && jsCode.length > 0 && jsCode.length < 100) {
          // Try to extract meaningful action from JavaScript code
          const actionMatch = jsCode.match(/(?:function|=>)\s*\([^)]*\)\s*\{?\s*([^}]+)/i) || 
                             jsCode.match(/(\w+)\(/i);
          // Fix Bug 2: Check if actionMatch exists AND has a captured group at index 1 before accessing it
          // The first regex might match without capturing a group, making actionMatch[1] undefined
          if (actionMatch && actionMatch[1] !== undefined && actionMatch[1] !== null) {
            const action = actionMatch[1].replace(/[(){};]/g, '').trim();
            // Verify action is non-empty after processing
            if (action && action.length > 0) {
              link.setAttribute('aria-label', action.charAt(0).toUpperCase() + action.slice(1) + ' action');
            } else {
              link.setAttribute('aria-label', 'Execute action');
            }
          } else {
            link.setAttribute('aria-label', 'Execute action');
          }
        } else {
          // Fallback for complex or empty JavaScript links
          link.setAttribute('aria-label', 'Execute action');
        }
      }
    });
  }

  fixColorContrast() {
    // Add visual indicators for low contrast (development only)
    // This function only runs in development environments for debugging purposes
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isDevelopment) {
      // This is a development helper - skip in production
      return;
    }
    
    // Ensure focus indicators have sufficient contrast without overriding
    // theme-managed text/search input focus styles.
    const style = document.createElement('style');
    style.textContent = `
      a:focus-visible,
      button:focus-visible,
      [role="button"]:focus-visible,
      [tabindex]:not([tabindex="-1"]):focus-visible,
      input[type="checkbox"]:focus-visible,
      input[type="radio"]:focus-visible,
      input[type="submit"]:focus-visible,
      input[type="button"]:focus-visible {
        outline: 3px solid #667eea !important;
        outline-offset: 2px !important;
      }
      a:focus, button:focus, [role="button"]:focus {
        outline: 3px solid #667eea !important;
        outline-offset: 2px !important;
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize accessibility fixes (singleton pattern to prevent duplicate initialization)
let accessibilityFixInstance = null;

function initAccessibilityFix() {
  if (!accessibilityFixInstance) {
    accessibilityFixInstance = new AccessibilityFix();
  }
  return accessibilityFixInstance;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccessibilityFix);
} else {
  initAccessibilityFix();
}

// Re-run fixes on window load for any late-loaded content (without creating new instance)
window.addEventListener('load', () => {
  if (accessibilityFixInstance) {
    accessibilityFixInstance.fixLinkNames();
    accessibilityFixInstance.fixAriaHiddenConflicts();
  }
}); 
